import { InfoCircleOutlined } from "@ant-design/icons"
import { Form, Modal, Space, Switch, Tooltip, Typography } from "antd"
import React from "react"

import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput"
import {
  CUSTOMER_FIRST_NAME_LABEL,
  CUSTOMER_FIRST_NAME_PLACEHOLDER
} from "../../../../constants/customer-name.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { normalizeEmail } from "../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../utils/validation/validate-phone"
import { customerEditableStatuses } from "../../constants/customer-statuses.ts"
import type {
  Customer,
  CustomerMutateData
} from "../../types/customer.types.ts"
import { CustomerFormActions } from "./CustomerFormActions.tsx"
import { CustomerFormHeader } from "./CustomerFormHeader.tsx"

const { Text } = Typography

interface CustomerFormProps {
  canInvite: boolean
  initialValues?: Partial<Customer>
  onSave: (values: CustomerMutateData) => void
  onCancel: () => void
}

export const CustomerForm = ({
  canInvite,
  initialValues,
  onSave,
  onCancel
}: CustomerFormProps) => {
  const [form] = Form.useForm<CustomerMutateData>()
  const saving = useAppSelector((state) => state.customers.saveFetching)

  const isEdit = !!initialValues

  const isEditableStatus = initialValues?.status
    ? customerEditableStatuses?.includes(initialValues?.status?.toLowerCase())
    : true

  const onFinish = (values: CustomerMutateData) => {
    onSave(values)
  }

  return (
    <Modal
      open
      onCancel={onCancel}
      footer={null}
      width={520}
      title={<CustomerFormHeader isEdit={isEdit} form={form} />}
      maskClosable={false}
      centered
    >
      <Form<CustomerMutateData>
        form={form}
        layout="vertical"
        onFinish={onFinish}
        validateTrigger={["onSubmit", "onChange"]}
        className={"with-default-error"}
        initialValues={
          initialValues || ({ is_invite: canInvite } as CustomerMutateData)
        }
        disabled={saving}
      >
        {/* Basic Information */}
        <Form.Item<CustomerMutateData>
          label={CUSTOMER_FIRST_NAME_LABEL}
          name="first_name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Please enter customer first name"
            }
          ]}
        >
          <TextInput
            placeholder={CUSTOMER_FIRST_NAME_PLACEHOLDER}
            disabled={!isEditableStatus}
            size={"large"}
          />
        </Form.Item>
        <Form.Item<CustomerMutateData>
          label="Middle Name"
          name="middle_name"
          rules={[
            {
              required: false,
              whitespace: true,
              message: "Please enter customer middle name"
            }
          ]}
        >
          <TextInput
            placeholder="Enter middle name"
            disabled={!isEditableStatus}
            size={"large"}
          />
        </Form.Item>
        <Form.Item<CustomerMutateData>
          label="Last Name"
          name="last_name"
          rules={[
            {
              required: false,
              whitespace: true,
              message: "Please enter customer last name"
            }
          ]}
        >
          <TextInput
            placeholder="Enter last name"
            disabled={!isEditableStatus}
            size={"large"}
          />
        </Form.Item>
        <Form.Item<CustomerMutateData>
          label="Email"
          name="email"
          normalize={normalizeEmail}
          rules={[
            {
              required: isEdit ? !!initialValues?.email : true,
              message: "Please enter email"
            },
            {
              type: "email",
              message: "Please enter a valid email"
            }
          ]}
        >
          <TextInput
            disableAutoCapitalize
            pattern={"[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}"}
            placeholder="example@ezbuild.com"
            disabled={!isEditableStatus}
            size={"large"}
          />
        </Form.Item>
        <Form.Item<CustomerMutateData>
          label="Phone Number"
          name="phone"
          rules={[{ required: false, validator: validatePhone }]}
        >
          <PhoneInput size={"large"} disabled={!isEditableStatus} />
        </Form.Item>

        {isEdit ? (
          <Form.Item<CustomerMutateData> label="Comment" name="notes">
            <TextArea
              size={"large"}
              rows={4}
              showCount
              maxLength={500}
              placeholder="Please, add your comment…"
              className="mb-4"
            />
          </Form.Item>
        ) : canInvite ? (
          <Form.Item>
            <Space>
              <Form.Item<CustomerMutateData>
                noStyle
                name="is_invite"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Text>Send invitation</Text>
              <Tooltip title="If disabled, the customer will be added without invitation.">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          </Form.Item>
        ) : null}

        {/* Form Actions */}
        <CustomerFormActions
          form={form}
          saving={saving}
          onCancel={onCancel}
          isEdit={isEdit}
        />
      </Form>
    </Modal>
  )
}
